"use client";

import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { cva } from "class-variance-authority";
import { CircleIcon } from "lucide-react";

import { cn } from "../../lib/utils";

const radioGroupVariants = cva("flex gap-2", {
	variants: {
		orientation: {
			vertical: "flex-col",
			horizontal: "flex-row flex-wrap",
		},
	},
	defaultVariants: {
		orientation: "vertical",
	},
});

const RadioGroup = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, orientation = "vertical", ...props }, ref) => (
	<RadioGroupPrimitive.Root
		ref={ref}
		data-slot="radio-group"
		orientation={orientation}
		className={cn(radioGroupVariants({ orientation }), className)}
		{...props}
	/>
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => (
	<RadioGroupPrimitive.Item
		ref={ref}
		data-slot="radio-group-item"
		className={cn(
			"peer h-5 w-5 shrink-0 rounded-full border border-neutral-300 bg-background outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 data-[state=checked]:border-success data-[state=checked]:bg-success disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:bg-danger-50 aria-[invalid=true]:border-2 aria-[invalid=true]:border-danger data-[state=checked]:aria-[invalid=true]:bg-danger-300 data-[state=checked]:aria-[invalid=true]:border-danger-300",
			className,
		)}
		{...props}
	>
		<RadioGroupPrimitive.Indicator
			data-slot="radio-group-indicator"
			className="flex items-center justify-center"
		>
			<CircleIcon className="size-2.5 fill-control-mark stroke-control-mark" />
		</RadioGroupPrimitive.Indicator>
	</RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

type RadioGroupCardItemProps = React.ComponentPropsWithoutRef<
	typeof RadioGroupPrimitive.Item
> & {
	label: string;
	description?: React.ReactNode;
};

const RadioGroupCardItem = React.forwardRef<
	React.ElementRef<typeof RadioGroupPrimitive.Item>,
	RadioGroupCardItemProps
>(({ className, label, description, id, ...props }, ref) => {
	const generatedId = React.useId();
	const radioId = id ?? generatedId;
	return (
		<label
			htmlFor={radioId}
			className={cn(
				"bg-background flex cursor-pointer gap-3 rounded-md border border-neutral-200 p-3 transition-colors hover:bg-neutral-50 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 dark:border-neutral-800 dark:hover:bg-neutral-900",
				"has-[[data-state=checked]]:border-neutral-900 dark:has-[[data-state=checked]]:border-neutral-50 dark:has-[[data-state=checked]]:bg-neutral-900",
				description ? "items-start" : "items-center",
				className,
			)}
		>
			<RadioGroupItem
				ref={ref}
				id={radioId}
				className={cn(description && "mt-0.5")}
				{...props}
			/>
			<div className="flex flex-col">
				{/* leading-snug, not leading-none: option text wraps to several lines
				    on long-form questions, and at leading-none those lines touch. */}
				<span className="text-sm font-medium leading-snug">{label}</span>
				{description && (
					<span className="mt-1 text-left text-sm text-neutral-500">
						{description}
					</span>
				)}
			</div>
		</label>
	);
});
RadioGroupCardItem.displayName = "RadioGroupCardItem";

export { RadioGroup, RadioGroupItem, RadioGroupCardItem };
